import { FC, ReactNode, RefCallback } from "react";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Tooltip } from "../Tooltip";
import styled from "styled-components";

/** The `Image` component properties. */
export interface ImageProps extends BaseProps {

  /** The image source. */
  readonly src: string;

  /** The image alternative text, required but can be empty string. */
  readonly alt: string;

  /** The optional width of the image. If the value is a number, it's considered pixels. */
  readonly width?: number | string;

  /** The optional height of the image. If the value is a number, it's considered pixels. */
  readonly height?: number | string;

  /** The image tooltip. */
  readonly tooltip?: ReactNode;
}

/**
 * Converts the specified numeric or string value to an appropriate CSS size.
 * @param value The value to get a CSS size from.
 * @returns The CSS size.
 */
function getSize(value?: number | string): string {
  if (value === undefined) return "unset";
  if (typeof value === "string") return value;
  return `${value}px`;
}

/** The styled image. */
const StyledImage = styled.img<{ imageWidth?: number | string; imageHeight?: number | string }>`
  display: inline-block;
  border: 0;
  margin: 0;
  width: ${(props) => getSize(props.imageWidth)};
  height: ${(props) => getSize(props.imageHeight)};
`;

/** A component which displays an image. */
export const Image: FC<ImageProps> = (props) => {
  const { src, tooltip, width, height, alt } = props;

  const htmlProps = getHtmlPropsFromBaseProps(props, {
    className: "image",
  });

  const content = (refHandler?: RefCallback<HTMLElement>): ReactNode => {
    return (
      <StyledImage
        {...htmlProps}
        ref={refHandler}
        src={src}
        imageWidth={width}
        imageHeight={height}
        alt={alt}
      />
    );
  };

  if (tooltip)
    return <Tooltip content={tooltip}>{content}</Tooltip>;

  return <>{content()}</>;
};
